import React, { useRef } from "react";
import { MentionEditor } from "./Editor";
import { ThreadMenu } from "./ThreadMenu";
import { ThreadActions } from "./ThreadActions";
import { CommentsList } from "./CommentsList";
import { formatDate } from "../utils/date";
import { Pin } from "lucide-react";
import { FilePreview } from "./FilePreview";
import { InView } from "react-intersection-observer";

export function ThreadCard({
  backendUrl,
  thread,
  comments,
  currentUserId,
  currentUserEmail,
  currentUserUsername,
  profilePicture,
  onComment,
  onEditThread,
  onEditComment,
  onToggleAnchor,
  onCommentView,
  onLike,
  onCommentLike,
  avatar,
  handleSeen,
  onDeleteThread,
  onDeleteComment,
  setIsEditing,
  isEditing,
}) {
  const [showCommentEditor, setShowCommentEditor] = React.useState(false);
  const [replyingTo, setReplyingTo] = React.useState(null);
  const [showThreadMenu, setShowThreadMenu] = React.useState(false);
  const [editingThread, setEditingThread] = React.useState(false);
  const [editingComment, setIsEditingComment] = React.useState(false);
  const [showCommentsSection, setShowCommentsSection] = React.useState(false);
  const [removedFiles, setRemovedFiles] = React.useState([]);

  const menuRef = useRef(null);
  const isCurrentUserThread = +currentUserId === thread?.user?.id;

  React.useEffect(() => {
    function handleClickOutside(event) {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowThreadMenu(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleComment = (content, files, selectedUser, selectedGroup) => {
    setIsEditingComment(true);
    onComment(thread.id, content, files, selectedUser, selectedGroup);
    setShowCommentEditor(false);
    setReplyingTo(null);
  };

  const handleEditThread = (content, files) => {
    if (onEditThread) {
      onEditThread(thread.id, content, files, removedFiles);
      setEditingThread(false);
      setRemovedFiles([]);
    }
  };

  const handleReply = (username) => {
    setReplyingTo(username);
    setShowCommentEditor(!showCommentEditor);
    setShowCommentsSection(!showCommentsSection);
  };

  const handleRemoveFile = (file) => {
    setRemovedFiles((prev) => [...prev, file]);
  };

  const handleDeleteThread = (threadID) => {
    onDeleteThread(threadID);
  };

  const handleDeleteComment = (commentID) => {
    onDeleteComment(commentID);
    setShowCommentEditor(false);
    setReplyingTo(null);
  };

  const isLiked =
    thread.likes.length > 0 &&
    thread.likes.some((like) => like.user?.username === thread.user?.username);

  return (
    <InView
      onChange={(inView) => {
        if (inView) {
          handleSeen(thread, "thread");
        }
      }}
      rootMargin={"100px"}
      threshold={1}
      key={thread.id}
    >
      <div
        className={`relative bg-white rounded-lg shadow-sm mb-8 transition-all duration-200 break-words${
          thread.anchored
            ? "border border-blue-100 bg-gradient-to-br from-blue-50/50 via-white to-white shadow-blue-100/50"
            : "border border-gray-200"
        }`}
      >
        {thread.anchored && (
          <div className="absolute -left-0.5 -top-0.5 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-tl-lg rounded-br-lg p-1.5 shadow-lg shadow-blue-500/20">
            <Pin size={14} className="stroke-[2.5]" />
          </div>
        )}

        <div
          className={`p-4 border rounded-lg ${thread.anchored ? "pt-6" : ""}`}
          id={thread.id}
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={
                      thread?.user?.avatar?.url
                        ? `${backendUrl}${thread.user.avatar.url}`
                        : profilePicture
                    }
                    alt={thread?.user?.username}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex flex-col justify-center h-10">
                  <h3 className="font-semibold leading-4 text-lg">
                    {thread?.user?.username}
                  </h3>
                  <span className="text-sm text-gray-600">
                    {formatDate(thread?.createdAt)}
                  </span>
                </div>
              </div>

              <div className="relative" ref={menuRef}>
                <ThreadMenu
                  show={showThreadMenu}
                  onToggle={() => setShowThreadMenu(!showThreadMenu)}
                  isCurrentUserThread={isCurrentUserThread}
                  isAnchored={!!thread.anchored}
                  onAnchor={
                    onToggleAnchor
                      ? () => {
                          onToggleAnchor(thread);
                          setShowThreadMenu(false);
                        }
                      : undefined
                  }
                  onEdit={() => {
                    setEditingThread(true);
                    setShowThreadMenu(false);
                  }}
                  handleDeleteThread={() => {
                    handleDeleteThread(thread);
                    setShowThreadMenu(false);
                  }}
                />
              </div>
            </div>

            <div className="pl-[52px]">
              {editingThread ? (
                <MentionEditor
                  onPublish={handleEditThread}
                  initialContent={thread.content}
                  buttonText="Save"
                  existingFiles={thread.media}
                  onRemoveExistingFile={handleRemoveFile}
                  backendUrl={backendUrl}
                  editingThread={editingThread}
                  isEditing={isEditing}
                  setIsEditing={setIsEditing}
                />
              ) : (
                <>
                  {thread.content && (
                    <div
                      className="text-gray-800 prose prose-md break-words"
                      dangerouslySetInnerHTML={{ __html: thread.content }}
                    />
                  )}
                  {thread.media && thread.media.length > 0 && (
                    <FilePreview files={thread.media} backendUrl={backendUrl} />
                  )}
                </>
              )}

              <div className="mt-3">
                <ThreadActions
                  likes={thread.likes || []}
                  likedBy={thread.likes || []}
                  isLiked={isLiked}
                  commentsCount={thread.comments?.length || 0}
                  showCommentEditor={showCommentEditor}
                  views={thread.seen_by || []}
                  onLike={() => onLike?.(thread.id, null)}
                  onComment={() => {
                    if (showCommentEditor && !replyingTo) {
                      setShowCommentEditor(false);
                    } else {
                      setShowCommentEditor(!showCommentEditor);
                      setReplyingTo(thread?.user?.username);
                      setShowCommentsSection(!showCommentsSection);
                    }
                  }}
                  backendUrl={backendUrl}
                  profilePicture={profilePicture}
                  avatar={avatar}
                />
              </div>
            </div>
          </div>

          {(showCommentsSection || thread.comments?.length > 0) && (
            <div className="mt-6 pl-12 space-y-4">
              {showCommentEditor && !replyingTo && (
                <div className="flex items-center gap-2 border border-gray-200 px-3 py-2 rounded-lg">
                  <div className="w-full">
                    <MentionEditor
                      onPublish={handleComment}
                      placeholder="Write a comment..."
                      buttonText="Comment"
                      initialContent={``}
                      backendUrl={backendUrl}
                      existingFiles={thread.media}
                      onRemoveExistingFile={handleRemoveFile}
                      editingComment={editingComment}
                      isEditing={isEditing}
                      setIsEditing={setIsEditing}
                    />
                  </div>
                </div>
              )}

              <CommentsList
                comments={comments || []}
                currentUserId={currentUserId}
                currentUserEmail={currentUserEmail}
                currentUserUsername={currentUserUsername}
                onReply={handleReply}
                onEdit={(commentId, content, files) => {
                  setIsEditingComment(true);
                  if (onEditComment) {
                    onEditComment(
                      thread.id,
                      commentId,
                      content,
                      files,
                      removedFiles
                    );
                  }
                }}
                onLike={(_, commentId) => onCommentLike?.(_, commentId)}
                onView={(commentId) => {
                  if (onCommentView) {
                    onCommentView(thread.id, commentId);
                  }
                }}
                showCommentEditor={showCommentEditor}
                replyingTo={replyingTo}
                onComment={handleComment}
                backendUrl={backendUrl}
                profilePicture={profilePicture}
                avatar={avatar}
                handleSeen={handleSeen}
                handleRemoveFile={handleRemoveFile}
                handleDeleteComment={handleDeleteComment}
                setIsEditing={setIsEditing}
                isEditing={isEditing}
              />

              {showCommentEditor && replyingTo && (
                <div className="border-l-2 border-blue-200 pl-4">
                  <div className="rounded-lg">
                    <MentionEditor
                      onPublish={handleComment}
                      placeholder={`Responder a @${replyingTo.username}...`}
                      buttonText="Responder"
                      initialContent={`@${replyingTo} `}
                      backendUrl={backendUrl}
                      existingFiles={
                        thread?.comments?.find(
                          (comment) => comment?.user?.username === replyingTo
                        )?.media || []
                      }
                      onRemoveExistingFile={handleRemoveFile}
                      isEditing={isEditing}
                      setIsEditing={setIsEditing}
                      isComment={true}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </InView>
  );
}
